import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useYearbook } from '../context/YearbookContext';
import { Analytics } from '../utils/analytics';
import { 
  UserGroupIcon, 
  HeartIcon, 
  HomeIcon,
  CheckCircleIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';

const yearbookTypes = [
  {
    id: 'group',
    name: 'Group Memory Book',
    description: 'Perfect for friend groups, work teams, and clubs. Celebrate your shared moments and inside jokes.',
    icon: UserGroupIcon,
    features: ['Multiple participants', 'Group dynamics', 'Shared memories'],
    color: 'rust',
  },
  {
    id: 'couple',
    name: 'Couple Memory Book',
    description: 'Your love story told through messages. Great for anniversaries or just because.',
    icon: HeartIcon,
    features: ['Two-person chat', 'Romantic tone options', 'Anniversary perfect'],
    color: 'forest',
  },
  {
    id: 'family',
    name: 'Family Memory Book',
    description: 'Turn your family group chaos into cherished memories. Parents, siblings, extended family.',
    icon: HomeIcon,
    features: ['Family-friendly', 'Multi-generation', 'Holiday highlights'],
    color: 'gold',
  },
];

export default function TypeSelectPage() {
  const navigate = useNavigate();
  const { yearbookType, setYearbookType } = useYearbook();
  
  const handleSelect = (typeId) => {
    setYearbookType(typeId);
  };
  
  const handleContinue = () => {
    if (yearbookType) {
      Analytics.trackTypeSelected(yearbookType);
      navigate('/upload');
    }
  };
  
  return (
    <div className="max-w-4xl mx-auto px-4 py-12">
      {/* Header */}
      <div className="text-center mb-12">
        <h1 className="font-display text-3xl sm:text-4xl font-bold text-ink mb-4">
          What are you making?
        </h1>
        <p className="text-sepia text-lg">
          Choose the type that best fits your chat
        </p>
      </div>
      
      {/* Type Cards */}
      <div className="grid gap-4 mb-12">
        {yearbookTypes.map((type) => {
          const isSelected = yearbookType === type.id;
          const Icon = type.icon;
          
          return (
            <button
              key={type.id}
              onClick={() => handleSelect(type.id)}
              className={`
                relative w-full text-left p-6 rounded-xl border-2 transition-all duration-200
                ${isSelected 
                  ? 'border-rust bg-white shadow-book' 
                  : 'border-parchment bg-white shadow-paper hover:border-sepia/40 hover:shadow-book'
                }
              `}
            >
              {/* Selection indicator */}
              <div className="absolute top-4 right-4">
                {isSelected ? (
                  <CheckCircleIcon className="w-7 h-7 text-rust" />
                ) : (
                  <ChevronRightIcon className="w-5 h-5 text-sepia/30" />
                )}
              </div>
              
              <div className="flex gap-5">
                {/* Icon */}
                <div className={`
                  flex-shrink-0 w-14 h-14 rounded-xl flex items-center justify-center
                  ${type.color === 'rust' ? 'bg-rust/10' : ''}
                  ${type.color === 'forest' ? 'bg-forest/10' : ''}
                  ${type.color === 'gold' ? 'bg-gold/20' : ''}
                `}>
                  <Icon className={`
                    w-7 h-7
                    ${type.color === 'rust' ? 'text-rust' : ''}
                    ${type.color === 'forest' ? 'text-forest' : ''}
                    ${type.color === 'gold' ? 'text-gold' : ''}
                  `} />
                </div>
                
                {/* Content */}
                <div className="flex-1 min-w-0 pr-8">
                  <h3 className="font-display text-xl font-semibold text-ink mb-1">
                    {type.name}
                  </h3>
                  <p className="text-sepia text-sm mb-3">
                    {type.description}
                  </p>
                  
                  {/* Features */}
                  <div className="flex flex-wrap gap-2">
                    {type.features.map((feature) => (
                      <span
                        key={feature}
                        className="px-2.5 py-1 bg-parchment/50 rounded-full text-xs text-sepia"
                      >
                        {feature}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </button>
          );
        })}
      </div>
      
      {/* Continue Button */}
      <div className="flex justify-center">
        <button
          onClick={handleContinue}
          disabled={!yearbookType}
          className={`
            btn-primary text-lg px-10 py-4
            ${!yearbookType ? 'opacity-50 cursor-not-allowed' : ''}
          `}
        >
          Continue
        </button>
      </div>
    </div>
  );
}

